import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
    Upload, FileSpreadsheet, CheckCircle2, AlertTriangle,
    XCircle, Download,
} from "lucide-react";

const EXAMPLE_CSV =
    "name,email,role\nAlex Doe,alex@example.com,student\nBhaskar K,bhaskar@example.com,sme\n";

/**
 * Dialog for bulk-creating users from a CSV upload. POSTs the file to
 * `/admin/users/import-csv` and renders the structured summary the
 * backend returns (per-row outcomes for invalid emails, duplicates,
 * already-existing users, email-send failures, etc.).
 *
 * Why two states? We deliberately keep the user on the result screen
 * after a successful import so they can audit which rows actually
 * landed before closing — anyone who's imported a CSV blindly into a
 * CRM knows the pain.
 */
export default function ImportUsersCsvDialog({ open, onOpenChange, onImported }) {
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => {
        setFile(null);
        setResult(null);
        setSubmitting(false);
    };

    const handleClose = (next) => {
        if (!next) reset();
        onOpenChange(next);
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!file) {
            toast.error("Choose a CSV file first.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/users/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            const c = data?.summary?.created || 0;
            if (c > 0) toast.success(`${c} user${c === 1 ? "" : "s"} imported.`);
            else toast.warning("No users were imported — see the report.");
            onImported?.();
        } catch (err) {
            const detail = err?.response?.data?.detail || "Import failed.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    };

    const downloadTemplate = () => {
        const blob = new Blob([EXAMPLE_CSV], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "heritage-users-template.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent
                className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
                data-testid="import-csv-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileSpreadsheet className="h-5 w-5 text-primary" />
                        Bulk import users from CSV
                    </DialogTitle>
                    <DialogDescription>
                        Upload a CSV with columns <code>name</code>,{" "}
                        <code>email</code>, and optional <code>role</code>{" "}
                        (student / instructor / sme). Each new user receives a
                        password-setup email automatically.
                    </DialogDescription>
                </DialogHeader>

                {!result ? (
                    <form onSubmit={submit} className="space-y-5">
                        <div className="rounded-lg border border-border bg-secondary/30 px-4 py-3 text-sm flex items-start justify-between gap-3">
                            <div className="space-y-1 flex-1 min-w-0">
                                <div className="font-medium">Format example</div>
                                <pre className="text-xs bg-background border border-border rounded px-2 py-1.5 font-mono overflow-x-auto">
{EXAMPLE_CSV}
                                </pre>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={downloadTemplate}
                                data-testid="download-csv-template"
                                className="flex-shrink-0"
                            >
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                Template
                            </Button>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="csv-file">CSV file</Label>
                            <Input
                                id="csv-file"
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                data-testid="csv-file-input"
                                required
                            />
                            {file && (
                                <div className="text-xs text-muted-foreground">
                                    Selected: <strong>{file.name}</strong> ({Math.ceil((file.size || 0) / 1024)} KB)
                                </div>
                            )}
                        </div>

                        <DialogFooter className="gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => handleClose(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting || !file}
                                data-testid="csv-import-submit"
                            >
                                <Upload className="h-4 w-4 mr-2" />
                                {submitting ? "Importing…" : "Import users"}
                            </Button>
                        </DialogFooter>
                    </form>
                ) : (
                    <ImportResult result={result} onClose={() => handleClose(false)} onReset={reset} />
                )}
            </DialogContent>
        </Dialog>
    );
}

function ImportResult({ result, onClose, onReset }) {
    const s = result.summary || {};
    return (
        <div className="space-y-5" data-testid="csv-import-result">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Stat label="Created" value={s.created || 0} tone="success" />
                <Stat label="Already existed" value={s.already_exists || 0} tone="muted" />
                <Stat label="Duplicates" value={s.duplicate_in_file || 0} tone="muted" />
                <Stat label="Invalid email" value={s.invalid || 0} tone="warn" />
                <Stat label="Email failed" value={s.email_failed || 0} tone="warn" />
            </div>

            <RowSection title="Created" rows={result.created} tone="success" emptyText="None" />
            <RowSection title="Already in system" rows={result.already_exists} tone="muted" emptyText="None" />
            <RowSection title="Duplicate inside CSV" rows={result.duplicate_in_file} tone="muted" emptyText="None" />
            <RowSection title="Invalid email" rows={result.invalid} tone="warn" emptyText="None" />
            <RowSection title="Email send failed" rows={result.email_failed} tone="warn" emptyText="None" />

            <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={onReset} data-testid="csv-import-another">
                    Import another file
                </Button>
                <Button onClick={onClose} data-testid="csv-import-close">
                    Done
                </Button>
            </DialogFooter>
        </div>
    );
}

function Stat({ label, value, tone }) {
    const cls = {
        success: "bg-emerald-50 text-emerald-700 border-emerald-200",
        warn: "bg-amber-50 text-amber-800 border-amber-300",
        muted: "bg-secondary text-muted-foreground border-border",
    }[tone] || "bg-secondary";
    return (
        <div className={`rounded-md border ${cls} px-3 py-2 text-center`}>
            <div className="font-display text-2xl leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-wider mt-1">{label}</div>
        </div>
    );
}

function RowSection({ title, rows, tone, emptyText }) {
    if (!rows || rows.length === 0) return null;
    const Icon = tone === "warn" ? AlertTriangle : tone === "success" ? CheckCircle2 : XCircle;
    return (
        <details className="rounded-md border border-border bg-card" open={rows.length <= 5}>
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium flex items-center gap-2">
                <Icon className="h-4 w-4 text-muted-foreground" />
                {title}
                <Badge variant="outline" className="ml-auto">{rows.length}</Badge>
            </summary>
            <ul className="px-4 pb-3 pt-1 space-y-1 text-xs">
                {rows.slice(0, 50).map((r, i) => (
                    <li key={i} className="font-mono break-all">
                        <span className="text-muted-foreground">row {r.row}:</span>{" "}
                        {r.email || emptyText}
                        {r.reason && <span className="text-muted-foreground"> — {r.reason}</span>}
                    </li>
                ))}
                {rows.length > 50 && (
                    <li className="text-muted-foreground">
                        + {rows.length - 50} more…
                    </li>
                )}
            </ul>
        </details>
    );
}
